/**
 * DashExportDialog — 报表导出弹窗（ADM-DASH-S010，移植自原型 dialogs3.jsx DashExportDialog）
 *
 * 接真（原型用 setTimeout 假进度 + window.toast 假成功 → 改真后端同步生成 + 下载中心）：
 *  - 新建导出：POST /api/v1/dashboard/export { app_scope, range } 同步生成 → 返终态记录
 *    （COMPLETED → 可下载；FAILED → 脱敏 error 文案 + 可重试）。成功后 invalidate 导出记录列表。
 *  - 导出记录：GET /api/v1/dashboard/exports（运营共享视图，created_at 倒序）。
 *  - 下载：GET /api/v1/dashboard/exports/:id/download 认证代理流（dashboardApi.download）。
 *  - 重试：FAILED 记录无「重提交同一条」端点 → 用相同 app_scope/range 重新 POST /export 生成新记录。
 *  - CSV 列定义表：静态文档，**与 server buildCsv（dashboard-export-admin-service.ts）实际输出同源**。
 *    行为类 section（DAU / 漏斗 / 留存）仅埋点就绪时导出，未就绪剔除；不导 alerts section、
 *    Top Books 不含 category（A0 §2 导出口径）→ 弹窗只列真实导出的 section/列，不杜撰
 *    （adversarial F3：旧版宣称含 DAU/readers/告警 section，与 server 实际不一致）。
 *
 * 偏离记账（见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）：
 *  - 原型「删除导出记录」：契约无 DELETE /exports/:id 端点（下载中心为只读共享视图）→ 不接入，
 *    该行操作按钮移除（不做假成功 toast）。
 *  - 原型进度条「62% / 412 行 / 38KB」：同步生成无中间进度，改为「生成中…」spinner + 真实
 *    row_count；文件大小后端不返 → 不展示。
 */

import type { DashboardExportRecord, DashboardRange } from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { dashboardApi } from '~/hooks/use-dashboard';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

// 生成中 spinner 样式（与 PaywallPage 同一 id 的一次性注入，已注入则 no-op；照原型保留）
if (typeof document !== 'undefined' && !document.getElementById('pw-spinner-style')) {
  const s = document.createElement('style');
  s.id = 'pw-spinner-style';
  s.textContent = `
    .pw-spinner {
      width: 10px; height: 10px;
      border: 1.5px solid var(--fg-faint);
      border-top-color: var(--accent);
      border-radius: 999px;
      display: inline-block;
      animation: pw-spin 0.7s linear infinite;
      margin-right: 4px;
    }
    @keyframes pw-spin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(s);
}

/**
 * CSV section / 列定义文档 —— 与 server buildCsv（dashboard-export-admin-service.ts）实际输出逐 section 对齐。
 * 行为类（dau / dau_series / funnel / retention）仅埋点就绪时导出、未就绪剔除；无 alerts section、
 * Top Books 不含 category（A0 §2 导出口径），故此处不列这些字段（adversarial F3：不杜撰）。
 * 列名用 CSV 实际 header（# KPI/value、# Revenue Trend/iap·stripe 等），与导出文件表头一字不差。
 */
const CSV_COLUMNS: Array<{ group: string; cols: string; note: string }> = [
  {
    group: '# KPI',
    cols: 'total_revenue / paying_users / dau / arppu / passive_refund_rate（列：value, delta_pct/pp）',
    note: 'USD 运营口径 · dau 仅埋点就绪时导出',
  },
  {
    group: '# Revenue Trend',
    cols: 'date, iap, stripe',
    note: '逐日 · 渠道闭集 IAP / Stripe · 不含 PayPal',
  },
  {
    group: '# Revenue By App',
    cols: 'app_id, app_name, revenue, share_pct',
    note: '仅 scope=全部 App 时导出',
  },
  {
    group: '# Top Books',
    cols: 'novel_id, title, readers, payers, paid_chapters, revenue',
    note: 'revenue=解锁 Coin 口径 · readers=窗口活跃阅读 UV · 已注销用户折叠为 1（观测近似，非结算）· 不含分类',
  },
  {
    group: '# Paying Users Series',
    cols: 'date, paying_users',
    note: '逐日付费用户',
  },
  {
    group: '# Behavior Basis',
    cols: 'basis / ready_app_ids / missing_app_ids',
    note: '以下行为类 section 仅埋点就绪时导出 · 仅覆盖同意分析采集的用户',
  },
  { group: '# DAU Series', cols: 'date, dau', note: '逐日（24h 逐小时）去重 app_open 用户' },
  {
    group: '# Funnel',
    cols: 'step, event, users（另附 funnel_overall_rate）',
    note: '六级嵌套漏斗 · 打开 → 书城 → 详情 → 阅读 → 解锁 → 购买',
  },
  {
    group: '# Retention',
    cols: 'cohort_start, size, d0_pct … d9_pct',
    note: '最近 7 个完整周 cohort · 未完整过去的天数留空',
  },
];

export interface DashExportDialogProps {
  /** 当前页面时间窗（导出即该口径快照）*/
  range: DashboardRange;
  /** 当前 App scope：'all' | UUID（注入 export body app_scope）*/
  appScope: string;
  /** scope 显示名（弹窗副标题用：「全部 App」/ App 名）*/
  scopeLabel: string;
  /** 是否可写（root/admin/operator）；viewer 只看记录不能新建 */
  canExport: boolean;
  onClose: () => void;
}

export function DashExportDialog({
  range,
  appScope,
  scopeLabel,
  canExport,
  onClose,
}: DashExportDialogProps) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'new' | 'history'>('new');
  // 本次会话刚生成的记录（COMPLETED → 立即可下载 / FAILED → 显脱敏 error + 可重试）
  const [lastRecord, setLastRecord] = useState<DashboardExportRecord | null>(null);

  // 导出记录列表（运营共享视图）；切到 history tab 或新建成功后消费/刷新
  const exportsQuery = useQuery({
    queryKey: ['dashboard-exports'],
    queryFn: () => dashboardApi.listExports().then((r) => r.items),
  });
  const records = exportsQuery.data ?? [];

  // 提交导出：同步生成 → 返终态记录。COMPLETED/FAILED 都是 HTTP 200（业务终态），统一进 onSuccess。
  const exportMutation = useMutation({
    mutationFn: () => dashboardApi.export({ app_scope: appScope, range }),
    onSuccess: async (rec) => {
      setLastRecord(rec);
      if (rec.status === 'COMPLETED') {
        toast(`报表已生成 · ${rec.row_count ?? 0} 行`, { tone: 'pos', icon: 'IconCheckCircle' });
      } else {
        // FAILED：后端已落 status=FAILED + 脱敏 error（非异常）→ 错误 toast，不假成功
        toast(rec.error ?? '报表生成失败', { tone: 'neg', icon: 'IconAlert' });
      }
      // 新记录已落 dashboard_exports → 刷新下载中心列表
      await queryClient.invalidateQueries({ queryKey: ['dashboard-exports'] });
    },
    onError: (err) => {
      // 生成请求本身失败（鉴权 / 校验 / 503）→ 统一错误 toast（按 code 中文化）
      const msg = err instanceof AdminApiError ? err.message : '报表生成失败';
      toast(msg, { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 下载：认证代理流 → blob。失败按 AdminApiError 走错误 toast（不假成功）。
  const downloadMutation = useMutation({
    mutationFn: (rec: DashboardExportRecord) =>
      dashboardApi.download(rec.id, `dashboard_${rec.range}.csv`),
    onError: (err) => {
      const msg = err instanceof AdminApiError ? err.message : '下载失败';
      toast(msg, { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const generating = exportMutation.isPending;
  const downloading = downloadMutation.isPending;

  const footer =
    tab === 'new' ? (
      <>
        <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
          生成后写入下载中心 · 运营共享视图 · 失败将展示脱敏错误并可重试
        </div>
        <button className="btn" onClick={onClose}>
          取消
        </button>
        {lastRecord?.status === 'COMPLETED' ? (
          <button
            className="btn btn-accent"
            disabled={downloading}
            onClick={() => downloadMutation.mutate(lastRecord)}
          >
            <Icons.IconDownload size={11} />
            {downloading ? '下载中…' : '下载 CSV'}
          </button>
        ) : (
          <button
            className="btn btn-accent"
            disabled={!canExport || generating}
            title={canExport ? undefined : '仅 root / admin / operator 可导出报表'}
            onClick={() => exportMutation.mutate()}
          >
            {generating ? (
              '生成中…'
            ) : (
              <>
                <Icons.IconCheck size={11} />
                开始生成
              </>
            )}
          </button>
        )}
      </>
    ) : (
      <>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={onClose}>
          关闭
        </button>
      </>
    );

  return (
    <Dialog
      width={720}
      title="导出报表 · CSV"
      subtitle={`ADM-DASH-S010 · 时间窗 ${range} · ${scopeLabel} · 导出动作落审计日志 (action=dashboard_export)`}
      icon="IconDownload"
      onClose={onClose}
      footer={footer}
    >
      <div style={{ padding: 18 }}>
        <div className="seg" style={{ marginBottom: 14 }}>
          <button className={tab === 'new' ? 'on' : ''} onClick={() => setTab('new')}>
            新建导出
          </button>
          <button className={tab === 'history' ? 'on' : ''} onClick={() => setTab('history')}>
            导出记录{records.length > 0 ? ` · ${records.length}` : ''}
          </button>
        </div>

        {tab === 'new' && (
          <>
            {/* 状态卡：idle 提示 / 生成中 spinner / 完成或失败终态 */}
            <div
              className="card-flat"
              style={{ padding: 12, marginBottom: 14, background: 'var(--bg-subtle)' }}
            >
              <div className="hstack" style={{ gap: 10 }}>
                {!lastRecord && !generating && (
                  <>
                    <Icons.IconInfo size={13} style={{ color: 'var(--info)' }} />
                    <span style={{ fontSize: 12 }}>
                      同步生成 CSV · 完成后可立即下载，并写入「导出记录」
                    </span>
                  </>
                )}
                {generating && (
                  <>
                    <span className="pw-spinner" style={{ width: 12, height: 12 }} />
                    <span style={{ fontSize: 12 }}>
                      正在聚合 {range} · {scopeLabel} 的指标数据…
                    </span>
                  </>
                )}
                {!generating && lastRecord?.status === 'COMPLETED' && (
                  <>
                    <Icons.IconCheckCircle size={13} style={{ color: 'var(--pos)' }} />
                    <span style={{ fontSize: 12 }}>
                      生成完成 · <span className="mono">dashboard_{lastRecord.range}.csv</span> ·{' '}
                      {lastRecord.row_count ?? 0} 行
                    </span>
                  </>
                )}
                {!generating && lastRecord?.status === 'FAILED' && (
                  <>
                    <Icons.IconAlert size={13} style={{ color: 'var(--neg)' }} />
                    <span style={{ fontSize: 12, color: 'var(--neg)' }}>
                      生成失败 · {lastRecord.error ?? '请稍后重试'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* CSV 列定义 · 指标映射（静态文档）*/}
            <div className="label" style={{ marginBottom: 6 }}>
              CSV 列定义 · 指标映射
            </div>
            <Table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>分组</th>
                  <th>列</th>
                  <th>说明</th>
                </tr>
              </thead>
              <tbody>
                {CSV_COLUMNS.map((r) => (
                  <tr key={r.group}>
                    <td style={{ fontWeight: 500, fontSize: 12 }}>{r.group}</td>
                    <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                      {r.cols}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {tab === 'history' && (
          <Table className="tbl">
            <thead>
              <tr>
                <th>导出</th>
                <th>范围</th>
                <th>状态</th>
                <th className="num">行数</th>
                <th>创建</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{ textAlign: 'center', color: 'var(--fg-faint)', padding: '24px 0' }}
                  >
                    {exportsQuery.isLoading ? '加载中…' : '暂无导出记录'}
                  </td>
                </tr>
              )}
              {records.map((e) => (
                <tr key={e.id}>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {e.id}
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {e.range} · {e.app_scope === 'all' ? '全部 App' : '单 App'}
                    </span>
                  </td>
                  <td>
                    {e.status === 'COMPLETED' && (
                      <span className="chip chip-pos" style={{ fontSize: 10 }}>
                        已完成
                      </span>
                    )}
                    {e.status === 'FAILED' && (
                      <span
                        className="chip chip-neg"
                        style={{ fontSize: 10 }}
                        title={e.error ?? ''}
                      >
                        失败
                      </span>
                    )}
                  </td>
                  <td className="num mono" style={{ fontSize: 11 }}>
                    {e.row_count ?? '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                    {fmtDateTime(e.created_at)}
                  </td>
                  <td>
                    <div className="hstack" style={{ gap: 4, justifyContent: 'flex-end' }}>
                      {e.status === 'COMPLETED' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={downloading}
                          onClick={() => downloadMutation.mutate(e)}
                        >
                          <Icons.IconDownload size={11} />
                          下载
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </Dialog>
  );
}
